import { Button } from "@/components/ui/button";
import { Truck, Flag } from "lucide-react";
import { ShipmentInfo } from "@/types";
import { useShiftDisplay } from "@/hooks/useShiftDisplay";
import { cn } from "@/lib/utils";

/**
 * Resuelve routeSubId (doble ciclo) a texto mostrable: si viene como array
 * JSON-encodeado (ej: '["A"]') une sus items con "/"; si no parsea como JSON
 * usa el string tal cual; null/vacío devuelve "".
 */
const resolveRouteSubId = (routeSubId: string | null): string => {
  if (!routeSubId) return "";
  try {
    const parsed = JSON.parse(routeSubId);
    if (Array.isArray(parsed)) return parsed.join("/");
  } catch {
    // No era JSON: se usa el string tal cual.
  }
  return routeSubId;
};

export type ShipmentCardProps = {
  shipment: ShipmentInfo;
  dayLabel: "Hoy" | "Mañana";
  onConfirm: (shipment: ShipmentInfo) => void;
  onCancel: (shipment: ShipmentInfo) => void;
  onStart: (shipment: ShipmentInfo) => void;
  onEnd: (shipment: ShipmentInfo) => void;
  onOpenStops: (stops: NonNullable<ShipmentInfo["stops"]>) => void;
};

export const ShipmentCard: React.FC<ShipmentCardProps> = ({
  shipment,
  dayLabel,
  onConfirm,
  onCancel,
  onStart,
  onEnd,
  onOpenStops,
}) => {
  const shiftDisplay = useShiftDisplay();
  return (
    <div
      data-testid="shipment-card"
      className={cn(
        "w-full p-4 rounded-lg border",
        shipment.status === "PENDIENTE"
          ? dayLabel === "Hoy"
            ? "bg-yellow-100 text-yellow-800 border-yellow-300"
            : "bg-purple-100 text-purple-800 border-purple-300"
          : shipment.status === "EN_CURSO"
          ? "bg-primary-soft text-primary-soft-foreground border-primary/30"
          : "bg-gray-100 text-gray-800 border-gray-300"
      )}
    >
      <div className="text-center">
        <div className="text-sm font-bold mb-1">
          Ruta Asignada de {dayLabel} -{" "}
          {shipment.status === "PENDIENTE"
            ? "Pendiente"
            : shipment.status === "EN_CURSO"
            ? "En Curso"
            : shipment.status}
        </div>
        <div className="text-xs space-y-1">
          <div>
            <span className="font-medium">Zona:</span> {shipment.svc}
          </div>
          <div>
            <span className="font-medium">Turno:</span>{" "}
            <span translate="no">{shiftDisplay.forOperations(shipment.shift, [shipment.operationId])}</span>
          </div>
          <div>
            <span className="font-medium">Hora:</span> {shipment.time}
          </div>
          <div>
            <span className="font-medium">Dirección:</span> {shipment.address}
          </div>
          {shipment.routeId && (
            <div>
              <span className="font-medium">Ruta:</span>{" "}
              <span>
                {shipment.routeId}
                {resolveRouteSubId(shipment.routeSubId) &&
                  ` · ${resolveRouteSubId(shipment.routeSubId)}`}
              </span>
            </div>
          )}
        </div>
        {shipment.stops && shipment.stops.length > 0 && (
          <div className="mt-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-primary border-primary/30 hover:bg-primary-soft flex items-center justify-center gap-2"
              onClick={() => onOpenStops(shipment.stops!)}
            >
              <Flag className="h-4 w-4" />
              Paradas ({shipment.stops.length})
            </Button>
          </div>
        )}
        {shipment.status === "PENDIENTE" && (
          <div className="flex space-x-2 mt-3 justify-center">
            <Button
              variant="outline"
              size="sm"
              className="text-green-600 border-green-300 hover:bg-green-50"
              onClick={() => onConfirm(shipment)}
            >
              Confirmar
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
              onClick={() => onCancel(shipment)}
            >
              No puedo ir
            </Button>
          </div>
        )}
        {shipment.status === "CONFIRMADO" && (
          <div className="flex space-x-2 mt-3 justify-center">
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
              onClick={() => onCancel(shipment)}
            >
              No puedo ir
            </Button>
          </div>
        )}
        {dayLabel === "Hoy" && (shipment.status === "PENDIENTE" || shipment.status === "CONFIRMADO") && (
          <Button
            className="w-full mt-3 h-12 text-base justify-start px-4 flex items-center hover:bg-primary/90"
            variant="default"
            onClick={() => onStart(shipment)}
          >
            <Truck className="mr-3 h-5 w-5 flex-shrink-0" />
            <span className="font-medium">Iniciar viaje</span>
          </Button>
        )}
        {dayLabel === "Hoy" && shipment.status === "EN_CURSO" && (
          <Button
            className="w-full mt-3 h-12 text-base justify-start px-4 flex items-center bg-primary hover:bg-primary/90"
            onClick={() => onEnd(shipment)}
          >
            <Flag className="mr-3 h-5 w-5 flex-shrink-0" />
            <span className="font-medium">Finalizar viaje</span>
          </Button>
        )}
      </div>
    </div>
  );
};
